import { css } from '@emotion/react'
import Router from 'next/router'
import { colors, CompanyLogo, Divider, font, mq, Tag } from 'plume-ui'
import * as React from 'react'
import { useUser } from '../../../../../contexts/AuthContext'
import {
  companyDefaultImage,
  isExited,
  isIPO,
  isPrivate,
  isResearchContinue,
  isResearchDone,
  isResearchUnvalued,
} from '@/lib/company'
import { formatWithComma, thousandToMillion } from '@/lib/number'
import { companySearchPath } from '@/lib/url'
import {
  isEnterpriseWithTrial,
  isModerator,
  isLatestEnterpriseOrTrial,
  isGuest,
} from '@/lib/user'
import {
  CompanyContactPerson,
  CompanyData,
  CompanyMemo,
  EmployeesDateType,
  Status,
  TagData,
  UsersResult,
} from '../../../../../types/api'
import { A } from '../../../../atoms/A'
import { IcomoonIcon } from '../../../../atoms/IcomoonIcon'
import { TagButton, TagButtonGroup } from '../../../../atoms/TagButton'
import { Help } from '../../../../common/Help'
import { CompanyFollowButton } from './CompanyFollowButton'
import { ContactPersonIcon } from '../ContactPersonIcon'
import { Description } from '../Description'
import { StatusIcon } from '../StatusIcon'
import { SubCommandMenu } from '../../SubCommandMenu'
import { MemoIcon } from '../MemoIcon'
import { IGNORED_BY_WOVN_CLASS_NAME } from '@/lib/wovn'
import { useState } from 'react'
import { checkSummaryStickyPosition } from '@/lib/sticky'
import { FormattedDate } from '../../../../atoms/FormattedDate'
import { CompanyAndInvestorSummaryBg } from '@/components/common/CompanyAndInvestorSummaryBg'
import Link from 'next/link'

type Props = {
  company: Required<CompanyData>
  showModal: () => void
  setIsStatusPanelVisible: React.Dispatch<React.SetStateAction<boolean>>
  companyStatus: Status | null
  contactPerson: CompanyContactPerson | null
  statuses: Status[]
  users: UsersResult
  companyMemo: CompanyMemo | null
  selectedStatus: Status | null
  setSelectedStatus: React.Dispatch<React.SetStateAction<Status | null>>
  selectedContactPerson: CompanyContactPerson | null
  setSelectedContactPerson: React.Dispatch<
    React.SetStateAction<CompanyContactPerson | null>
  >
}

export const CompanySummary: React.FC<Props> = (props) => {
  const user = useUser()

  const [isCompanySummaryStickyPosition, setIsCompanySummaryStickyPosition] =
    useState(false)

  React.useEffect(() => {
    window.addEventListener('scroll', () => {
      setIsCompanySummaryStickyPosition(checkSummaryStickyPosition)
    })
    return () => {
      window.removeEventListener('scroll', () => {
        setIsCompanySummaryStickyPosition(checkSummaryStickyPosition)
      })
    }
  }, [])

  return (
    <div
      css={css`
        display: inline;
      `}
      data-test-id="summary"
    >
      <EyecatchBg
        tags={props.company.tags}
        sticky={isCompanySummaryStickyPosition}
        css={css`
          ${mq.large} {
            margin-block-start: 24px;
          }
        `}
      >
        <div
          css={css`
            position: absolute;
            z-index: 1;
            display: flex;
            justify-content: space-between;
            width: 100%;
            padding-block-start: 12px;
            padding-inline: 24px;
            ${mq.large} {
              position: static;
              padding-inline: 16px 38px;
            }
          `}
        >
          {isModerator(user) && (
            <p
              css={css`
                margin: 0;
              `}
            >
              <EditLogoLink shortname={props.company.shortname} />
            </p>
          )}
          <p
            css={css`
              margin: 0;
              color: ${colors.white00};
              font-size: 12px;
              line-height: 1.25;
              letter-spacing: 0.02em;
            `}
          >
            {(isResearchDone(props.company) ||
              isResearchUnvalued(props.company)) && (
              <>
                最終更新日:{' '}
                <span className={IGNORED_BY_WOVN_CLASS_NAME}>
                  <FormattedDate>{props.company.lastUpdatedAt}</FormattedDate>
                </span>
              </>
            )}
          </p>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-end;
            width: 100%;
            max-width: ${isCompanySummaryStickyPosition && '1120px'};
            margin-block-start: auto;
            margin-inline: auto;
            padding-inline: 24px;
            color: ${colors.textInvert};
            ${mq.large} {
              padding-inline: ${isCompanySummaryStickyPosition ? '0' : '20px'};
            }
            ${mq.extraLarge} {
              padding-inline: ${isCompanySummaryStickyPosition ? '0' : '50px'};
            }
          `}
        >
          <div
            css={css`
              display: flex;
              align-items: ${isCompanySummaryStickyPosition ? 'center' : 'end'};
              justify-content: space-between;
              column-gap: 24px;
              width: 100%;
              ${mq.large} {
                min-height: ${isCompanySummaryStickyPosition ? '96px' : '80px'};
              }
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: start;
                flex-direction: column;
                column-gap: ${isCompanySummaryStickyPosition ? '16px' : '32px'};
                row-gap: 16px;
                min-width: 0;
                margin-block-start: 148px;
                ${mq.large} {
                  align-items: ${isCompanySummaryStickyPosition
                    ? 'center'
                    : 'end'};
                  flex-direction: row;
                  margin: 0;
                }
              `}
            >
              <div
                css={css`
                  flex-grow: 0;
                  flex-shrink: 0;
                  ${mq.large} {
                    margin-block-end: ${isCompanySummaryStickyPosition
                      ? '0'
                      : '-80px'};
                  }
                `}
                data-test-id="logo"
              >
                <CompanyLogo
                  name={props.company.name}
                  src={
                    props.company.imageUrl ||
                    companyDefaultImage(props.company.recordType)
                  }
                  size={
                    isCompanySummaryStickyPosition ? 'normal' : 'extra-large'
                  }
                  css={css`
                    ${mq.untilMedium} {
                      width: 96px !important;
                      height: 96px !important;
                    }
                  `}
                />
              </div>
              <div
                css={css`
                  display: flex;
                  flex-direction: column;
                  row-gap: 4px;
                  width: 100%;
                  min-width: 0;
                  margin-block-end: 0;
                  ${mq.large} {
                    row-gap: 2px;
                    margin-block-end: ${isCompanySummaryStickyPosition
                      ? '0'
                      : '20px'};
                  }
                `}
              >
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                    column-gap: 16px;
                  `}
                >
                  <dl
                    className="corporate-number"
                    css={css`
                      margin: 0;
                      display: flex;
                      line-height: 1.25;
                      color: ${colors.textLight};
                      font-size: 13px;
                      ${mq.large} {
                        color: ${colors.textInvert};
                        font-size: 14px;
                      }
                    `}
                  >
                    <dt
                      css={css`
                        &::after {
                          content: '：' / '';
                        }
                      `}
                    >
                      法人番号
                    </dt>
                    <dd
                      css={css`
                        margin: 0;
                      `}
                      className={IGNORED_BY_WOVN_CLASS_NAME}
                      data-test-id="corporate-number"
                    >
                      {props.company.corporateNumber || '-'}
                    </dd>
                  </dl>
                  {(isResearchDone(props.company) ||
                    isResearchUnvalued(props.company)) && (
                    <p
                      css={css`
                        margin: 0;
                      `}
                    >
                      <Tag
                        inverted
                        square
                        color="red"
                        css={css`
                          font-size: 12px;
                        `}
                      >
                        {isIPO(props.company)
                          ? `IPO（${props.company.researchStatus}）`
                          : props.company.researchStatus}
                      </Tag>
                    </p>
                  )}
                </div>
                <h1
                  css={css`
                    margin: 0;
                    color: ${colors.text};
                    font-size: 24px;
                    font-weight: ${font.weight.bold};
                    line-height: 1.5;
                    letter-spacing: 0.01em;
                    overflow-wrap: break-word;
                    transition: font-size 0.16s 0s linear;
                    ${mq.large} {
                      color: ${colors.textInvert};
                      font-size: ${isCompanySummaryStickyPosition
                        ? '28px'
                        : '32px'};
                      line-height: 1.25;
                      ${isCompanySummaryStickyPosition &&
                      'text-overflow: ellipsis; overflow: hidden; white-space: nowrap;'}
                    }
                  `}
                  data-test-id="name"
                >
                  {props.company.name}
                </h1>
              </div>
            </div>
            <div
              css={css`
                display: none;
                ${mq.large} {
                  display: flex;
                  justify-content: space-between;
                  column-gap: 16px;
                  margin-block-end: ${isCompanySummaryStickyPosition
                    ? '0'
                    : '8px'};
                }
              `}
            >
              {isLatestEnterpriseOrTrial(user) && (
                <div
                  css={css`
                    flex-shrink: 0;
                    display: flex;
                    column-gap: 4px;
                  `}
                >
                  <StatusIcon
                    shortname={props.company.shortname}
                    statuses={props.statuses}
                    selectedStatus={props.selectedStatus}
                    setSelectedStatus={props.setSelectedStatus}
                  />
                  <ContactPersonIcon
                    shortname={props.company.shortname}
                    selectedContactPerson={props.selectedContactPerson}
                    setSelectedContactPerson={props.setSelectedContactPerson}
                    users={props.users}
                  />
                  <MemoIcon
                    companyMemo={props.companyMemo}
                    setIsStatusPanelVisible={props.setIsStatusPanelVisible}
                  />
                </div>
              )}
              <div
                css={css`
                  display: flex;
                  align-items: start;
                  column-gap: 8px;
                  margin-block-start: 12px;
                `}
              >
                <CompanyFollowButton
                  shortname={props.company.shortname}
                  isEnterprise={isEnterpriseWithTrial(user)}
                  showModal={props.showModal}
                  css={css``}
                />
                <SubCommandMenu company={props.company} />
              </div>
            </div>
          </div>
        </div>
      </EyecatchBg>
      <div
        css={css`
          display: flex;
          flex-direction: column;
          max-width: 1200px;
          margin: 0 auto;
          min-height: 200px;
        `}
      >
        <div
          css={css`
            padding: 0 24px 10px;
            ${mq.large} {
              margin-left: 180px;
              padding: 0px 20px 0px 32px;
            }
            font-size: 14px;
            font-weight: 400;
            line-height: 150%;
          `}
        >
          {isGuest(user) &&
          isResearchDone(props.company) &&
          !isExited(props.company) ? (
            <div
              className="research-finished-message"
              css={css`
                padding-top: 16px;
              `}
            >
              本企業は、スピーダ スタートアップ情報リサーチの
              <Link
                href="https://help.initial.inc/ja/articles/4570807"
                passHref
              >
                <A target="_blank" decoration="none">
                  スタートアップの定義
                </A>
              </Link>
              に則り、
              <FormattedDate>{props.company.lastUpdatedAt}</FormattedDate>
              をもって調査を終了しました。
            </div>
          ) : (
            <>
              <Description company={props.company} />
              <SummaryTags company={props.company} />
            </>
          )}
        </div>
        {(!isGuest(user) ||
          isResearchContinue(props.company) ||
          isResearchUnvalued(props.company) ||
          isExited(props.company)) && (
          <>
            <Divider
              dashed
              css={css`
                width: 90%;
                margin: 5px auto;
                ${mq.large} {
                  display: none;
                }
              `}
            />
            <SummaryTable company={props.company} showModal={props.showModal} />
          </>
        )}
      </div>
    </div>
  )
}

const SummaryTags: React.FC<{
  company: CompanyData
}> = (props) => {
  return (
    <div
      css={css`
        margin-top: 10px;
      `}
    >
      {props.company.tags.length > 0 ? (
        <TagButtonGroup>
          {props.company.tags.map((tag, i) => (
            <TagButton key={i} tag={tag} />
          ))}
        </TagButtonGroup>
      ) : (props.company.searchKeywords || []).length > 0 ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            margin-bottom: 15px;
          `}
        >
          <div
            css={css`
              color: ${colors.textStrong};
              font-size: 13px;
              font-weight: ${font.weight.bold};
              margin-right: 10px;
            `}
          >
            検索キーワード：
          </div>
          <div
            css={css`
              display: flex;
              flex-wrap: wrap;
            `}
          >
            {(props.company.searchKeywords || []).map((k, i) => {
              const isLast =
                (props.company.searchKeywords || []).length === i + 1
              return (
                <span
                  key={k}
                  css={css`
                    margin-right: 8px;
                  `}
                >
                  <a
                    href={companySearchPath(k)}
                    target="_blank"
                    css={css`
                      font-size: 13px;
                      color: ${colors.text};
                      transition: 0.3s;
                      &:hover {
                        color: ${colors.link};
                        text-decoration: underline;
                      }
                    `}
                  >
                    {k}
                  </a>
                  {isLast || <span>,</span>}
                </span>
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

const SummaryTable: React.FC<{
  company: CompanyData
  showModal: () => void
}> = (props) => {
  const user = useUser()
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        margin-bottom: 18px;
        ${mq.large} {
          flex-direction: row;
        }
      `}
    >
      <div
        css={css`
          display: flex;
          padding: 12px 20px 5px;
          justify-content: space-between;
          flex-wrap: wrap;
          ${mq.large} {
            padding-left: 30px;
            padding-right: 30px;
            width: 50%;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            ${mq.large} {
              justify-content: flex-start;
            }
          `}
        >
          <>
            <SummaryLabel>
              スピーダ
              <br
                css={css`
                  ${mq.large} {
                    display: none;
                  }
                `}
              />
              調達シリーズ
              <a
                href={process.env.INITIAL_SERIES_HELP_ENDPOINT}
                target="_blank"
                rel="noopener"
                css={css`
                  color: ${colors.gray90};
                `}
              >
                <Help
                  css={css`
                    vertical-align: bottom;
                  `}
                >
                  スピーダが独自に定義する成長フェーズ
                </Help>
              </a>
            </SummaryLabel>
            {isPrivate(props.company) ? (
              <SeriesName series={props.company.latestInitialSeries} />
            ) : (
              <div
                css={css`
                  text-align: center;
                  font-size: 1.3rem;
                  line-height: 1.4;
                  font-size: 500;
                  color: ${colors.black30};
                  ${mq.large} {
                    font-size: 2rem;
                  }
                `}
              >
                -
              </div>
            )}
            {isPrivate(props.company) ? (
              <DateNote date={props.company.lastValuationDate} suffix="時点" />
            ) : (
              <span
                css={css`
                  font-size: 12px;
                  color: ${colors.gray70};
                `}
              >
                （調査終了）
              </span>
            )}
          </>
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            ${mq.large} {
              justify-content: flex-start;
            }
          `}
        >
          {isPrivate(props.company) ? (
            <>
              <SummaryLabel className="post-money-valuation">
                調達後評価額
                <br
                  css={css`
                    ${mq.large} {
                      display: none;
                    }
                  `}
                />
                <SummaryLabelAnnotation>
                  {props.company.withoutDilutiveShares
                    ? '（潜在株を含まない）'
                    : '（潜在株を含む）'}
                </SummaryLabelAnnotation>
              </SummaryLabel>
              <EmphasizedNumber
                unit={`百万円${
                  props.company.hasEstimatedRound ? '（推測）' : ''
                }`}
                testDataId="post-money-valuation"
              >
                {props.company.valuation ? (
                  formatWithComma(thousandToMillion(props.company.valuation))
                ) : (
                  <EmptyNumber />
                )}
              </EmphasizedNumber>
              <DateNote date={props.company.lastValuationDate} suffix="時点" />
            </>
          ) : (
            <>
              <SummaryLabel>
                IPO時 時価総額
                <br
                  css={css`
                    ${mq.large} {
                      display: none;
                    }
                  `}
                />
                (初値)
              </SummaryLabel>
              {props.company.ipoData?.ipoMarketCap && (
                <EmphasizedNumber
                  unit="百万円"
                  testDataId="post-money-valuation"
                >
                  {formatWithComma(props.company.ipoData.ipoMarketCap)}
                </EmphasizedNumber>
              )}
              <div></div>
            </>
          )}
        </div>
        <div
          css={css`
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: space-between;
            ${mq.large} {
              justify-content: flex-start;
            }
          `}
        >
          {isPrivate(props.company) ? (
            <SummaryLabel>
              従業員数
              <EmployeesReportTypeLabel data={props.company} />
            </SummaryLabel>
          ) : (
            <SummaryLabel>IPO時従業員数</SummaryLabel>
          )}
          {isEnterpriseWithTrial(user) ? (
            <>
              <EmphasizedNumber
                unit="人"
                testDataId="employees"
                className="employees"
              >
                {props.company.employeesCount ? (
                  formatWithComma(props.company.employeesCount)
                ) : (
                  <EmptyNumber />
                )}
              </EmphasizedNumber>
              <DateNote
                date={props.company.employeesEventDate}
                suffix={suffixFromDateType(props.company.employeesDateType)}
              />
            </>
          ) : (
            <div
              css={css`
                max-width: 120px;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
              className="guest-employees"
            >
              <img
                src="/static/images/companies/guest/startup-blur-employee.png"
                alt="従業員数"
              />
            </div>
          )}
        </div>
      </div>
      <Divider
        dashed
        css={css`
          width: 90%;
          margin: 10px auto 5px;
          ${mq.large} {
            display: none;
          }
        `}
      />
      <div
        css={css`
          padding: 12px 20px 5px;

          ${mq.large} {
            padding-right: 30px;
            flex: 50%;
            padding-left: 3.5rem;
          }
        `}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
          `}
        >
          <SummaryLabel>
            株主
            <SummaryLabelAnnotation>
              {isPrivate(props.company) ? '（過去の株主を含む）' : '（IPO時）'}
            </SummaryLabelAnnotation>
          </SummaryLabel>
          <MajorShareholderNames
            company={props.company}
            showModal={props.showModal}
          />
        </div>
      </div>
    </div>
  )
}

const EmployeesReportTypeLabel: React.FC<{ data: CompanyData }> = ({
  data,
}) => {
  if (!data.employeesReportType || data.employeesReportType === '不明') {
    return null
  } else {
    return (
      <SummaryLabelAnnotation>{`（${data.employeesReportType}）`}</SummaryLabelAnnotation>
    )
  }
}

const EditLogoLink: React.FC<{ shortname: string }> = (props) => {
  return (
    <div
      css={css`
        color: ${colors.white00};
        text-align: left;
      `}
    >
      <a
        href={`/admin/companies/${props.shortname}/logo`}
        css={css`
          color: ${colors.white00};
        `}
      >
        <IcomoonIcon
          name="gear"
          css={css`
            margin-right: 3px;
            vertical-align: bottom;
          `}
        />
        ロゴを設定
      </a>
    </div>
  )
}

type SummaryLabelProps = {
  className?: string
  children: React.ReactNode
}

export const SummaryLabel: React.FC<SummaryLabelProps> = (props) => {
  return (
    <div
      css={css`
        color: ${colors.black10};
        font-weight: ${font.weight.bold};
        margin-bottom: 12px;
        font-size: 13px;
      `}
      className={props.className}
    >
      {props.children}
    </div>
  )
}

type SummaryLabelAnnotationProps = {
  children: React.ReactNode
}

export const SummaryLabelAnnotation: React.FC<SummaryLabelAnnotationProps> = (
  props
) => {
  return (
    <span
      className="annotation"
      css={css`
        font-weight: ${font.weight.normal};
        font-size: 11px;
        ${mq.large} {
          font-size: 12px;
        }
      `}
    >
      {props.children}
    </span>
  )
}

const SeriesName: React.FC<{ series?: string | null }> = (props) => {
  if (!props.series) {
    return <EmptySeries />
  }

  const series =
    props.series === 'シード'
      ? 'シード'
      : props.series.match(/[A-G]/)?.[0]?.toUpperCase()

  const SeriesControl: React.FC<{ series: string | undefined }> = (props) => {
    if (props.series === 'シード') {
      return (
        <div
          css={css`
            font-size: 18px;
            font-weight: ${font.weight.bold};
            ${mq.large} {
              margin: 4px 0;
              font-size: 24px;
            }
          `}
        >
          {props.series}
        </div>
      )
    } else {
      return (
        <div
          css={css`
            font-size: 24px;
            ${mq.large} {
              font-size: 32px;
            }
          `}
          data-test-id="initial-series"
        >
          {props.series}
        </div>
      )
    }
  }

  return <SeriesControl series={series} />
}

type EmphasizedNumberProps = {
  unit: string
  testDataId: String
  children: React.ReactNode
  className?: string
}

const EmphasizedNumber: React.FC<EmphasizedNumberProps> = (props) => {
  return (
    <div className={props.className}>
      <span
        css={css`
          color: ${colors.black30};
          font-size: 24px;
          ${mq.large} {
            font-size: 32px;
          }
        `}
        className={IGNORED_BY_WOVN_CLASS_NAME}
        data-test-id={props.testDataId}
      >
        {props.children}
      </span>
      <span
        css={css`
          margin-left: 4px;
          font-size: 13px;
          color: ${colors.gray90};
        `}
      >
        {props.unit}
      </span>
    </div>
  )
}

const EmptyNumber: React.FC = () => {
  return (
    <span
      data-test-id="empty-number"
      css={css`
        padding-left: 1rem;
        padding-right: 1rem;
        font-size: 32px;
        &::after {
          content: '−';
        }
      `}
    />
  )
}

const EmptySeries: React.FC = () => {
  return (
    <span
      css={css`
        height: 40px;
        padding-left: 1rem;
        padding-right: 1rem;
        font-size: 32px;
        &::after {
          content: '−';
        }
      `}
    />
  )
}

const DATE_TYPE_MAPPING = {
  時点日: '時点',
  確認日: '確認',
  提供日: '提供',
}

const suffixFromDateType = (type: EmployeesDateType | null): string => {
  return type && DATE_TYPE_MAPPING[type] ? DATE_TYPE_MAPPING[type] : ''
}

export const DateNote: React.FC<{
  date?: string | null
  suffix: string | null
  className?: string
}> = (props) => {
  if (!props.date) {
    return <div></div>
  }

  return (
    <div
      className={props.className}
      css={css`
        margin-top: 5px;
        color: ${colors.textLight};
        white-space: nowrap;
      `}
    >
      <span
        css={css`
          display: inline-block;
          margin-right: 2px;
          font-size: 12px;
          ${mq.large} {
            font-size: 13px;
          }
        `}
        className={IGNORED_BY_WOVN_CLASS_NAME}
      >
        <FormattedDate>{props.date}</FormattedDate>
      </span>
      <span
        css={css`
          font-size: 12px;
          line-height: normal;
          letter-spacing: normal;
        `}
      >
        {props.suffix}
      </span>
    </div>
  )
}

type EyecatchBgProps = {
  tags: TagData[]
  className?: string
  sticky?: boolean
  children: React.ReactNode
}

export const EyecatchBg: React.FC<EyecatchBgProps> = (props) => {
  const TAG_EYECATCH_IMAGES = {
    software: 'software.jpg',
    healthtech: 'healthtech.jpg',
    ai: 'ai.jpg',
    adtech: 'adtech.jpg',
    hrtech: 'hrtech.png',
    iot: 'iot.jpg',
    content: 'contents.jpg',
    copyright: 'contents.jpg',
    fashion: 'fashion.jpg',
    lifestyle: 'fashion.jpg',
    fintech: 'fintech.png',
    cleantech: 'cleantech.jpg',
    semiconductor: 'hardware.jpg',
    hardware: 'hardware.jpg',
    'pharmaceutical-and-drug-discovery': 'pharmaceutical.jpg',
    travel: 'travel.jpg',
    'bio-chemicals': 'biochemical.jpg',
    agritech: 'agritech.jpg',
    'big-data': 'bigdata.jpg',
    automotivetech: 'automotivetech.jpg',
    architecture: 'architecture.jpg',
    logistics: 'logistics.jpg',
    space: 'univers.jpg',
    edtech: 'edtech.jpg',
    curation: 'curation.jpg',
  }

  const DEFAULT_EYECATCH_IMAGE = 'default.jpg'

  const eyecatchUrl = (tags: TagData[]) => {
    // NOTE: 紐づいているタグ内、先に来ているものが優先される
    const tag = tags.find((tag) =>
      Object.keys(TAG_EYECATCH_IMAGES).includes(tag?.slug || '')
    ) as TagData | undefined
    const tagSlug =
      tag && tag.slug ? (tag.slug as keyof typeof TAG_EYECATCH_IMAGES) : null
    return tagSlug ? TAG_EYECATCH_IMAGES[tagSlug] : DEFAULT_EYECATCH_IMAGE
  }

  const background =
    '/static/images/companies/eyecatch/' + eyecatchUrl(props.tags)

  return (
    <CompanyAndInvestorSummaryBg
      backgroundImage={background}
      sticky={props.sticky}
      className={props.className}
    >
      {props.children}
    </CompanyAndInvestorSummaryBg>
  )
}

const MajorShareholderNames: React.FC<{
  company: CompanyData
  showModal: () => void
}> = (props) => {
  const user = useUser()

  let names: string[]

  if (isPrivate(props.company)) {
    const majorInstitutionalShareholderNames =
      props.company.institutionalShareholders
        ?.slice(0, 5)
        .map((s) => s.investor.name) || []
    const majorAngelShareholderNames =
      props.company.angelShareholders
        ?.slice(0, 5)
        .map((s) => s.investor.name) || []

    names = majorInstitutionalShareholderNames.concat(
      majorAngelShareholderNames
    )
  } else {
    names =
      props.company.ipoShareholders?.slice(0, 10).map((s) => s.investor.name) ||
      []
  }

  if (names.length == 0) {
    return (
      <div
        css={css`
          color: ${colors.textLight};
          margin-top: 10px;
        `}
      >
        {isPrivate(props.company)
          ? '表示する情報がありません'
          : 'データ未作成です'}
      </div>
    )
  }

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault()
    if (!isEnterpriseWithTrial(user)) {
      props.showModal()
    } else {
      Router.push(`${props.company.path}#shareholders`)
    }
  }

  return (
    <div
      css={css`
        line-height: 1.5;
        font-size: 14px;
      `}
    >
      <span
        css={css`
          margin-right: 0.8em;
        `}
        data-test-id="shareholders"
      >
        {names.join(' / ')}
      </span>
      {names.length > 0 && (
        <A
          href="#shareholders"
          css={css`
            white-space: nowrap;
          `}
          onClick={handleClick}
        >
          すべて見る
        </A>
      )}
    </div>
  )
}
